// src/pages/Disclaimer.jsx
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

export default function Disclaimer() {
    const [mounted, setMounted] = useState(false)

    useEffect(() => {
        setMounted(true)
    }, [])

    return (
        <div className="min-h-screen flex flex-col bg-[#07090e] text-slate-300 antialiased relative overflow-x-hidden">
            {/* Ambient Background Radials for High Aesthetic Depth */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[350px] bg-gradient-to-b from-indigo-500/[0.03] to-transparent blur-[120px] pointer-events-none rounded-full" />
            <div className="absolute top-[40%] right-[-10%] w-[400px] h-[400px] bg-amber-500/[0.01] blur-[150px] pointer-events-none rounded-full" />

            <Navbar />

            {/* Core Consumer-Facing Container */}
            <main className="flex-1 max-w-5xl mx-auto w-full px-6 py-16 lg:py-24 relative z-10 flex flex-col justify-center">

                <div className={`w-full transition-all duration-1000 ease-out transform ${mounted ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
                    }`}>

                    {/* Centered Editorial Header */}
                    <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
                        <div className="inline-flex items-center gap-2.5 bg-brand-amber/[0.06] border border-brand-amber/25 rounded-full px-5 py-2 mb-5 backdrop-blur-sm">
                            <span className="w-2 h-2 rounded-full bg-brand-amber shadow-[0_0_8px_rgba(212,168,67,0.9)]" />
                            <span className="text-sm font-sans font-semibold tracking-widest text-brand-amber uppercase">
                                Service Scope Declaration
                            </span>
                        </div>
                        <h1 className="font-sans font-bold text-4xl sm:text-5xl text-white tracking-tight mb-4">
                            Disclaimer
                        </h1>
                        <p className="text-sm font-sans font-light text-slate-400 tracking-wide">
                            Last Updated: June 2026 &bull; Version 1.0
                        </p>
                    </div>

                    {/* Main Content Card */}
                    <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6 sm:p-10 shadow-2xl backdrop-blur-xl relative">

                        {/* Introduction */}
                        <div className="mb-8">
                            <p className="text-base font-body font-light text-slate-300 leading-[1.85]">
                                This Disclaimer explains the limitations of the Guardian Message Check ("GMC") platform and the responsibilities of users when using our services.
                            </p>
                            <p className="text-base font-body font-light text-slate-300 leading-[1.85] mt-4">
                                By accessing or using Guardian Message Check, you acknowledge and agree to the terms set out in this Disclaimer.
                            </p>
                            <p className="text-base font-body font-light text-slate-300 leading-[1.85] mt-4">
                                This Disclaimer should be read together with our Terms and Conditions, Privacy Policy and other applicable platform policies.
                            </p>
                        </div>

                        <div className="space-y-12 text-base font-body font-light text-slate-300 leading-[1.85]">

                            {/* Section 1 */}
                            <div>
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    1. Purpose of the Platform
                                </h2>
                                <p className="text-slate-400">
                                    Guardian Message Check is an AI-powered communication analysis platform designed to help users better understand written communications.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    The platform provides:
                                </p>
                                <ul className="list-disc list-inside text-slate-400 space-y-2 mt-3 ml-2">
                                    <li>communication risk analysis;</li>
                                    <li>plain-English explanations;</li>
                                    <li>communication insights;</li>
                                    <li>professional rewrite suggestions.</li>
                                </ul>
                                <p className="text-slate-400 mt-4">
                                    Guardian Message Check is intended to assist users in making more informed communication decisions.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    The platform is an informational tool and should not be regarded as a substitute for professional judgement.
                                </p>
                            </div>

                            {/* Section 2 */}
                            <div className="border-l-2 border-amber-500/40 pl-4 py-1">
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    2. Not Legal or Professional Advice
                                </h2>
                                <p className="text-slate-400">
                                    Guardian Message Check is not a law firm and does not provide legal advice.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    Nothing generated by the platform should be interpreted as legal, financial, employment, tax, medical, regulatory or any other form of professional advice.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    Users should seek advice from an appropriately qualified professional where specialist advice is required.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    Using Guardian Message Check does not create any solicitor-client, consultant-client or professional advisory relationship.
                                </p>
                            </div>

                            {/* Section 3 */}
                            <div>
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    3. AI-Generated Content
                                </h2>
                                <p className="text-slate-400">
                                    Guardian Message Check uses artificial intelligence to analyse communications and generate responses.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    Artificial intelligence can occasionally produce inaccurate, incomplete or unexpected outputs.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    While Guardian Message Check is designed to provide helpful, high-quality analysis, no guarantee is given that every result will be accurate, complete or suitable for a user's particular circumstances.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    Users should carefully review all analysis and rewrite suggestions before relying upon or sending any communication.
                                </p>
                            </div>

                            {/* Section 4 */}
                            <div>
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    4. User Responsibility
                                </h2>
                                <p className="text-slate-400">
                                    Users remain solely responsible for:
                                </p>
                                <ul className="list-disc list-inside text-slate-400 space-y-2 mt-3 ml-2">
                                    <li>deciding whether to use any platform output;</li>
                                    <li>reviewing all rewritten communications before sending them;</li>
                                    <li>ensuring submitted communications are lawful;</li>
                                    <li>ensuring they have the legal right to submit any content for analysis;</li>
                                    <li>all decisions and actions taken as a result of using Guardian Message Check.</li>
                                </ul>
                                <p className="text-slate-400 mt-4">
                                    Guardian Message Check provides assistance only.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    Final responsibility for every communication always remains with the user.
                                </p>
                            </div>

                            {/* Section 5 */}
                            <div className="border-l-2 border-amber-500/40 pl-4 py-1">
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    5. No Guarantee of Outcomes
                                </h2>
                                <p className="text-slate-400">
                                    Guardian Message Check cannot guarantee that using the platform will:
                                </p>
                                <ul className="list-disc list-inside text-slate-400 space-y-2 mt-3 ml-2">
                                    <li>prevent disputes;</li>
                                    <li>improve legal outcomes;</li>
                                    <li>avoid litigation;</li>
                                    <li>improve employment outcomes;</li>
                                    <li>guarantee successful negotiations;</li>
                                    <li>prevent complaints;</li>
                                    <li>eliminate communication risk.</li>
                                </ul>
                                <p className="text-slate-400 mt-4">
                                    The platform provides guidance designed to help users communicate more effectively, but every communication remains dependent upon its particular facts, context and the actions of the individuals involved.
                                </p>
                            </div>

                            {/* Section 6 */}
                            <div>
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    6. Limitation of Liability
                                </h2>
                                <p className="text-slate-400">
                                    Guardian Message Check is provided on an "as available" and "as is" basis.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    To the fullest extent permitted by applicable law, Guardian Message Check shall not be liable for any indirect, incidental, consequential, special or economic losses arising from or connected with the use of the platform.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    This includes, but is not limited to:
                                </p>
                                <ul className="list-disc list-inside text-slate-400 space-y-2 mt-3 ml-2">
                                    <li>loss of profits;</li>
                                    <li>loss of revenue;</li>
                                    <li>loss of business opportunities;</li>
                                    <li>loss of contracts;</li>
                                    <li>loss of goodwill;</li>
                                    <li>loss of reputation;</li>
                                    <li>loss of anticipated savings;</li>
                                    <li>business interruption;</li>
                                    <li>loss or corruption of data.</li>
                                </ul>
                                <p className="text-slate-400 mt-4">
                                    Users acknowledge that Guardian Message Check provides AI-assisted communication analysis only and that all decisions, actions and communications remain the sole responsibility of the user.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    Nothing in this Disclaimer excludes or limits any liability which cannot lawfully be excluded under the laws of England and Wales.
                                </p>
                            </div>

                            {/* Section 7 */}
                            <div>
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    7. Third-Party Services
                                </h2>
                                <p className="text-slate-400">
                                    Guardian Message Check relies on trusted third-party service providers to operate the platform, including providers responsible for secure payment processing, cloud infrastructure, authentication and email delivery.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    Guardian Message Check is not responsible for interruptions, delays or failures caused by third-party services that are outside our reasonable control.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    Links to third-party websites or services may be provided for user convenience. Guardian Message Check does not endorse or accept responsibility for the content, availability or privacy practices of third-party websites.
                                </p>
                            </div>

                            {/* Section 8 */}
                            <div>
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    8. Platform Availability
                                </h2>
                                <p className="text-slate-400">
                                    Guardian Message Check aims to provide a reliable and secure service but cannot guarantee uninterrupted availability.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    The platform may occasionally be unavailable due to:
                                </p>
                                <ul className="list-disc list-inside text-slate-400 space-y-2 mt-3 ml-2">
                                    <li>scheduled maintenance;</li>
                                    <li>software updates;</li>
                                    <li>emergency security work;</li>
                                    <li>infrastructure failures;</li>
                                    <li>internet connectivity issues;</li>
                                    <li>third-party service outages;</li>
                                    <li>events beyond our reasonable control.</li>
                                </ul>
                                <p className="text-slate-400 mt-4">
                                    Where reasonably practicable, advance notice of planned maintenance will be provided.
                                </p>
                            </div>

                            {/* Section 9 */}
                            <div>
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    9. Changes to This Disclaimer
                                </h2>
                                <p className="text-slate-400">
                                    Guardian Message Check may amend this Disclaimer from time to time to reflect changes in legislation, platform functionality, business operations or best practice.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    The most recent version will always be published on the Guardian Message Check website.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    Your continued use of the platform after any changes take effect constitutes acceptance of the updated Disclaimer.
                                </p>
                            </div>

                            {/* Section 10 */}
                            <div>
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    10. Contact Information
                                </h2>
                                <p className="text-slate-400">
                                    If you have any questions regarding this Disclaimer or the Guardian Message Check platform, please contact:
                                </p>
                                <div className="mt-3 p-4 bg-white/[0.02] border border-white/[0.04] rounded-xl">
                                    <p className="text-slate-300 font-medium">Guardian Message Check</p>
                                    <p className="text-slate-400 mt-2">Email: <a href="mailto:admin@guardianmessagecheck.com" className="mail-link">admin@guardianmessagecheck.com</a></p>
                                    <p className="text-slate-400">Website: guardianmessagecheck.com</p>
                                </div>
                                <p className="text-slate-400 mt-4">
                                    We aim to respond to genuine enquiries as promptly as reasonably practicable.
                                </p>
                            </div>

                            {/* Section 11 */}
                            <div className="bg-gradient-to-r from-amber-500/[0.06] to-transparent border border-amber-500/10 rounded-xl p-4">
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    11. Final Provisions
                                </h2>
                                <p className="text-slate-400">
                                    This Disclaimer should be read together with the following Guardian Message Check documents:
                                </p>
                                <ul className="list-disc list-inside text-slate-400 space-y-2 mt-3 ml-2">
                                    <li>Terms and Conditions</li>
                                    <li>Privacy Policy</li>
                                    <li>Cookies & Browser Storage Policy</li>
                                    <li>Zero Retention Policy</li>
                                </ul>
                                <p className="text-slate-400 mt-4">
                                    If any provision of this Disclaimer is found to be unlawful, invalid or unenforceable, the remaining provisions shall continue in full force and effect.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    This Disclaimer forms part of the overall legal framework governing the use of the Guardian Message Check platform.
                                </p>
                            </div>

                        </div>

                        {/* Back-Navigation Trigger & Footer Actions */}
                        <div className="mt-14 pt-8 border-t border-white/[0.05] flex flex-col sm:flex-row items-center justify-between gap-5">
                            <Link to="/register" className="back-link group">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="back-arrow">
                                    <line x1="19" y1="12" x2="5" y2="12"></line>
                                    <polyline points="12 19 5 12 12 5"></polyline>
                                </svg>
                                <span>Return to Account Creation</span>
                            </Link>

                            <a href="mailto:admin@guardianmessagecheck.com" className="mail-link text-sm font-sans font-semibold tracking-wide">
                                admin@guardianmessagecheck.com
                            </a>
                        </div>

                    </div>

                </div>
            </main>

            <Footer />
        </div>
    )
}